import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import NewsCard, { CategoryBadge } from "@/components/NewsCard";
import { CATEGORY_META, formatDate } from "@/lib/constants";
import { Clock } from "lucide-react";

export default function CategoryPage({ category }) {
  const meta = CATEGORY_META[category];
  const [items, setItems] = useState(null);
  useSeo({ title: meta.label, description: meta.desc });

  useEffect(() => {
    setItems(null);
    api.get(`/articles?category=${category}&limit=40`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [category]);

  const immersive = category === "especiales";
  const lead = items && items[0];
  const rest = items ? items.slice(1) : [];

  return (
    <div data-testid={`category-page-${category}`}>
      {/* header */}
      <section className="bg-nx-dark text-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="flex items-center gap-3">
            <span className="w-2 h-10 rounded-sm" style={{ backgroundColor: meta.color }} />
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em]" style={{ color: meta.color }}>Sección</p>
              <h1 className="font-serif font-black text-4xl md:text-6xl tracking-tight">{meta.label}</h1>
            </div>
          </div>
          <p className="mt-4 text-slate-300 max-w-2xl text-sm md:text-base">{meta.desc}</p>
        </div>
      </section>

      {!items ? (
        <div className="max-w-container mx-auto px-4 py-20 animate-pulse grid gap-8">
          {[1, 2, 3].map((i) => <div key={i} className="h-40 bg-gray-100 rounded" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="max-w-container mx-auto px-4 py-24 text-center text-gray-500">No hay artículos disponibles.</div>
      ) : immersive ? (
        <section className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {items.map((a, idx) => (
              <Link key={a.id} to={`/${a.category}/${a.slug}`} data-testid={`especial-${a.slug}`}
                className={`group relative overflow-hidden rounded-md ${idx % 3 === 0 ? "md:col-span-2 h-96" : "h-72"}`}>
                <img src={a.image} alt={a.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                <div className="absolute bottom-0 p-6 md:p-8 text-white max-w-3xl">
                  <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1" style={{ backgroundColor: meta.color }}>{a.subcategory || "Especial"}</span>
                  <h2 className={`font-serif font-black mt-3 leading-tight ${idx % 3 === 0 ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"}`}>{a.title}</h2>
                  <p className="mt-2 text-slate-200 text-sm line-clamp-2 max-w-xl">{a.subtitle}</p>
                  <div className="mt-3 text-xs text-slate-300 flex items-center gap-2">
                    <span>{a.author}</span><span>·</span><span>{formatDate(a.date)}</span>
                    <span>·</span><span className="flex items-center gap-1"><Clock size={12} />{a.reading_time} min</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : (
        <section className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-10">
            <div>
              {lead && <NewsCard article={lead} variant="hero" />}
              <div className="mt-4">
                {rest.map((a) => <NewsCard key={a.id} article={a} variant="list" />)}
              </div>
            </div>
            <aside className="lg:border-l lg:border-gray-200 lg:pl-8">
              <div className="sticky top-32">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-1.5 h-5 rounded-sm" style={{ backgroundColor: meta.color }} />
                  <h3 className="font-serif font-black text-lg text-nx-dark">Más de {meta.label}</h3>
                </div>
                {items.slice(0, 6).map((a) => <NewsCard key={`s-${a.id}`} article={a} variant="compact" />)}
              </div>
            </aside>
          </div>
        </section>
      )}
    </div>
  );
}
